import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { setSnackbar } from '../../../../../store';
import { useTranslation } from 'react-i18next';
import { useStyles } from '../CustomFilter.style';
import {
  Typography,
  Grid,
  TableCell,
  TableRow,
  TableBody,
  Tooltip,
  Chip,
  TableContainer,
  Table,
  TableHead,
  useTheme,
  TableSortLabel,
} from '@material-ui/core';
import SearchPatients from './SearchPatients.js/SearchPatients';
import PeopleIcon from '@material-ui/icons/People';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { formatPatientName } from '../../../../../helpers/misc';

export default function CreateList(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const theme = useTheme();
  const [order, setOrder] = useState('ASC');
  const [orderBy, setOrderBy] = useState('');

  const tableHeader = [
    { name: 'patientId', label: t('Filters.patient_id'), align: 'left', sortable: true },
    { name: 'name', label: t('Filters.name'), align: 'left', sortable: true },
    { name: 'dateOfBirth', label: t('Filters.dob'), align: 'left', sortable: true },
    { name: 'examDate', label: t('Filters.date'), align: 'left', sortable: true },
    { name: 'actions', label: '', align: 'left', sortable: false },
  ];

  const removePatient = (patientId) => {
    props?.setPatientList((prevList) => prevList.filter((user) => user.patientId !== patientId));
    dispatch(setSnackbar({ msg: 'Filters.patient_removed', severity: 'info', duration: 1000 }));
  };

  // Sorting functions
  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === 'DESC'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'ASC';
    setOrder(isAsc ? 'DESC' : 'ASC');
    setOrderBy(property);
  };

  const createSortHandler = (property) => (event) => {
    handleRequestSort(event, property);
  };

  return (
    <div className={classes.search_container}>
      <Typography variant='h6' className={classes.custom_filter_title}>
        {t('Filters.create_list_title')}
      </Typography>
      <Typography className={classes.custom_filter_description}>
        {t('Filters.create_list_helper_text')}
      </Typography>
      <Grid
        container
        wrap='nowrap'
        direction='row'
        spacing={1}
        style={{ background: 'rgb(63 63 63)', borderRadius: '5px', padding: '4px' }}>
        <SearchPatients
          setPatientList={props?.setPatientList}
          patientList={props?.patientList}
          size={7}
          style={{ borderRight: '1px solid gray' }}
        />

        <Grid xs={5} item className={classes.grid_container}>
          <div className={classes.table_title_container}>
            <Typography className={classes.table_title}>{t('Filters.custom_list_title')}</Typography>
            <div className={classes.table_title_container}>
              <Tooltip
                arrow
                placement='top-end'
                title={<Typography className={classes.tooltip_text}>{t('Filters.number_added')}</Typography>}>
                <Chip
                  label={`${props?.patientList?.length}`}
                  variant='outlined'
                  size='small'
                  color='secondary'
                  icon={<PeopleIcon style={{ margin: '4px' }} />}
                />
              </Tooltip>
            </div>
          </div>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {tableHeader.map((head, i) => (
                    <TableCell
                      key={i}
                      align={head.align}
                      className={classes.table_row}
                      sortDirection={orderBy === head.name ? order.toLowerCase() : false}>
                      {head.sortable ? (
                        <TableSortLabel
                          active={orderBy === head.name}
                          direction={orderBy === head.name ? order.toLowerCase() : 'asc'}
                          onClick={createSortHandler(head.name)}>
                          {head.label}
                        </TableSortLabel>
                      ) : (
                        head.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              {props?.patientList?.length > 0 ? (
                <TableBody>
                  {[...props?.patientList]
                    .sort(orderBy ? getComparator(order, orderBy) : () => 0)
                    .map((patient) => (
                      <TableRow key={patient?.patientId || patient?.studyId}>
                        <TableCell className={classes.table_row}>{patient?.patientId}</TableCell>
                        <TableCell className={classes.table_row}>
                          {formatPatientName(patient?.name)}
                        </TableCell>
                        <TableCell className={classes.table_row}>{patient?.dateOfBirth}</TableCell>
                        <TableCell className={classes.table_row}>{patient?.examDate}</TableCell>

                        <TableCell align='right' className={classes.table_row}>
                          <Tooltip
                            arrow
                            placement='top-start'
                            title={
                              <Typography className={classes.tooltip_text}>{t('Filters.remove')}</Typography>
                            }>
                            <HighlightOffIcon
                              fontSize='small'
                              color='secondary'
                              onClick={() => removePatient(patient?.patientId)}
                              style={{ cursor: 'pointer' }}
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              ) : (
                <TableBody>
                  <TableRow>
                    <TableCell
                      align={'center'}
                      colSpan={tableHeader.length}
                      variant='footer'
                      style={{ borderBottom: 'unset' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography
                          className={classes.table_placeholder}
                          style={{ fontSize: theme.fonts.responsiveMediumBig }}>
                          {t('Filters.placeholder_empty')}
                        </Typography>
                      </div>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </div>
  );
}
